import React, { useMemo, useState, useRef, useEffect } from 'react';
import useEventsOverview from '../../hooks/useEventsOverview';
import { Input, Space, Divider } from 'antd';
import { CloseCircleOutlined, SearchOutlined } from '@ant-design/icons';
import {
  StyledBody,
  SignalBox,
  SignalContent,
  SignalName,
  EmptyState,
  SignalMainPanel,
  DrawerCardWrapper,
} from './styled';

interface DrawerCardProps {
  isDrawerOpen: boolean;
  selectedEventNames: string[];
  onSelectionChange: (eventNames: string[]) => void;
}

const DrawerCard = ({ isDrawerOpen, selectedEventNames, onSelectionChange }: DrawerCardProps) => {
  const { eventList, isLoading } = useEventsOverview();

  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<any>(null);

  // Focus search input when drawer opens
  useEffect(() => {
    if (isDrawerOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [isDrawerOpen]);

  // Apply search filter and sort alphabetically
  const filteredEvents = useMemo(() => {
    const events =
      !eventList || !searchTerm
        ? eventList || []
        : eventList.filter((eventName) =>
            eventName.toLowerCase().includes(searchTerm.toLowerCase()),
          );

    return events.sort((a, b) => a.localeCompare(b));
  }, [eventList, searchTerm]);

  const toggleSelect = (eventName: string) => {
    const updated = selectedEventNames.includes(eventName)
      ? selectedEventNames.filter((e) => e !== eventName)
      : [...selectedEventNames, eventName];

    onSelectionChange(updated);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  if (isLoading) {
    return (
      <div>
        <div>Loading events...</div>
      </div>
    );
  }

  if (!eventList || eventList.length === 0) {
    return (
      <div>
        <div>No events available.</div>
      </div>
    );
  }

  return (
    <DrawerCardWrapper>
      <Space direction="vertical" size="middle" style={{ flexShrink: 0 }}>
        <Input
          ref={searchInputRef}
          placeholder="Search events..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          prefix={<SearchOutlined />}
          suffix={
            searchTerm ? (
              <CloseCircleOutlined
                onClick={handleClearSearch}
                style={{ cursor: 'pointer', color: '#999' }}
              />
            ) : null
          }
          allowClear={false}
        />
      </Space>

      <Divider size="small" style={{ flexShrink: 0 }} />

      {filteredEvents.length === 0 ? (
        <EmptyState>No events found matching your criteria.</EmptyState>
      ) : (
        <SignalMainPanel>
          {filteredEvents.map((eventName) => {
            const isSelected = selectedEventNames.includes(eventName);
            return (
              <SignalBox
                onClick={() => toggleSelect(eventName)}
                $isSelected={isSelected}
                key={eventName}
              >
                <StyledBody>
                  <SignalContent>
                    <SignalName>{eventName}</SignalName>
                  </SignalContent>
                </StyledBody>
              </SignalBox>
            );
          })}
        </SignalMainPanel>
      )}
    </DrawerCardWrapper>
  );
};

export default DrawerCard;
